import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil, filter, take, debounceTime } from 'rxjs';
import { Chart, registerables } from 'chart.js';

import { ChartService } from '../../../services/chart.service';
import { AgentService } from '../../../services/agent.service';
import { AuthService } from '../../../services/auth.service';
import {
  CumulativeChartDataDto,
  ChartGranularity,
  ChartFilterParams,
  EntityChartDataDto,
  CumulativeInsightsChartDataDto,
  InsightsChartDataPointDto
} from '../../../models/chart.models';

import { ChartWrapperComponent } from '../shared/chart-wrapper/chart-wrapper.component';
import { MultiSeriesChartComponent } from '../shared/multi-series-chart/multi-series-chart.component';
import { DateRangePickerComponent, DateRangeValue } from '../shared/date-range-picker/date-range-picker.component';
import { DistrictZoneFilterComponent } from '../../shared/district-zone-filter/district-zone-filter.component';

// Register Chart.js components
Chart.register(...registerables);

@Component({
  selector: 'app-cumulative-charts',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ChartWrapperComponent,
    MultiSeriesChartComponent,
    DateRangePickerComponent,
    DistrictZoneFilterComponent
  ],
  templateUrl: './cumulative-charts.component.html',
  styleUrl: './cumulative-charts.component.css'
})
export class CumulativeChartsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private filterChange$ = new Subject<void>();

  // Entities to exclude from time series charts (no temporal data)
  private excludedEntities = ['customer', 'customers'];

  // Entities to exclude from individual view (shown only in cumulative/running total)
  private individualViewExcludedEntities = ['subscription'];

  // Data
  cumulativeData: CumulativeChartDataDto | null = null;
  insightsData: CumulativeInsightsChartDataDto | null = null;

  // Loading states
  loadingCumulative = false;
  loadingInsights = false;
  errorCumulative = '';
  errorInsights = '';

  // Cached data for performance
  private cachedTimeSeriesEntities: EntityChartDataDto[] | null = null;
  private cachedCumulativeEntities: EntityChartDataDto[] | null = null;
  private cachedCumulativeRunningTotal: EntityChartDataDto[] | null = null;
  private cachedInsightsDailyData: EntityChartDataDto[] | null = null;
  private cachedInsightsCumulativeData: EntityChartDataDto[] | null = null;
  private cachedInsightsRevenueData: EntityChartDataDto[] | null = null;

  // Filters
  selectedGeoZones: string[] = [];
  selectedAgents: string[] = [];
  startDate = '';
  endDate = '';
  granularity: ChartGranularity = ChartGranularity.Daily;

  // Dropdown states
  agentDropdownOpen = false;

  // Dropdown options
  agents: { value: string; label: string }[] = [];

  // View options
  chartViewType: 'combined' | 'individual' | 'cumulative' | 'insights' = 'combined';
  currentTime = Date.now();
  private documentClickHandler: (event: Event) => void;

  constructor(
    private chartService: ChartService,
    private agentService: AgentService,
    private authService: AuthService
  ) {
    this.documentClickHandler = this.handleDocumentClick.bind(this);
  }

  ngOnInit(): void {
    document.addEventListener('click', this.documentClickHandler);
    // Wait for authentication
    this.authService.authReady$
      .pipe(
        filter(ready => ready === true),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.loadDropdownData();
      });

    // Setup debounced filter changes
    this.filterChange$
      .pipe(
        debounceTime(300),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.loadChartData();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    document.removeEventListener('click', this.documentClickHandler);
  }

  private handleDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;

    // Agent dropdown
    const agentDropdown = document.querySelector('#agentFilter')?.closest('.status-dropdown-container');
    if (agentDropdown && !agentDropdown.contains(target)) {
      this.agentDropdownOpen = false;
    }
  }

  /**
   * Load dropdown options for geo zones and agents
   */
  private loadDropdownData(): void {
    this.agentService.getAgentsForSelection()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (agents) => {
          this.agents = agents;
        },
        error: (error) => {
          console.error('Error loading dropdown data:', error);
        }
      });
  }

  /**
   * Handle date range change from picker
   */
  onDateRangeChange(dateRange: DateRangeValue): void {
    this.startDate = dateRange.startDate;
    this.endDate = dateRange.endDate;
    this.invalidateCache();
    this.filterChange$.next();
  }

  /**
   * Handle granularity change
   */
  onGranularityChange(granularity: ChartGranularity): void {
    this.granularity = granularity;
    this.invalidateCache();
    this.filterChange$.next();
  }

  /**
   * Apply filters and reload data
   */
  applyFilters(): void {
    this.invalidateCache();
    this.filterChange$.next();
  }

  /**
   * Reset all filters to default
   */
  resetFilters(): void {
    this.selectedGeoZones = [];
    this.selectedAgents = [];
    this.invalidateCache();
    this.filterChange$.next();
  }

  /**
   * Invalidate cached data
   */
  private invalidateCache(): void {
    this.cachedTimeSeriesEntities = null;
    this.cachedCumulativeEntities = null;
    this.cachedCumulativeRunningTotal = null;
    this.cachedInsightsDailyData = null;
    this.cachedInsightsCumulativeData = null;
    this.cachedInsightsRevenueData = null;
  }

  /**
   * Load chart data based on current view
   */
  private loadChartData(): void {
    // Only load data for the current view to improve performance
    if (this.chartViewType === 'insights') {
      this.loadInsightsData();
    } else {
      this.loadCumulativeData();
    }
  }

  /**
   * Load cumulative chart data
   */
  loadCumulativeData(): void {
    if (!this.startDate || !this.endDate) return;

    this.loadingCumulative = true;
    this.errorCumulative = '';

    const filters: ChartFilterParams = {
      startDate: new Date(this.startDate).toISOString(),
      endDate: new Date(this.endDate + 'T23:59:59').toISOString(),
      granularity: this.granularity,
      geoZoneIds: this.selectedGeoZones.length > 0 ? this.selectedGeoZones.join(',') : undefined,
      agentIds: this.selectedAgents.length > 0 ? this.selectedAgents.join(',') : undefined
    };

    this.chartService.getCumulativeChartData(filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {

          this.cumulativeData = data;
          this.invalidateCache(); // Invalidate cache when new data arrives
          this.loadingCumulative = false;
        },
        error: (error) => {
          console.error('Error loading cumulative data:', error);
          this.errorCumulative = this.getErrorMessage(error);
          this.loadingCumulative = false;
        }
      });
  }


  /**
   * Load insights chart data
   */
  loadInsightsData(): void {
    if (!this.startDate || !this.endDate) return;

    this.loadingInsights = true;
    this.errorInsights = '';

    const filters: ChartFilterParams = {
      startDate: new Date(this.startDate).toISOString(),
      endDate: new Date(this.endDate + 'T23:59:59').toISOString(),
      granularity: this.granularity,
      geoZoneIds: this.selectedGeoZones.length > 0 ? this.selectedGeoZones.join(',') : undefined,
      agentIds: this.selectedAgents.length > 0 ? this.selectedAgents.join(',') : undefined
    };

    this.chartService.getInsightsChartData(filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {

          this.insightsData = data;
          this.invalidateCache(); // Invalidate insights cache when new data arrives
          this.loadingInsights = false;
        },
        error: (error) => {
          console.error('Error loading insights data:', error);
          this.errorInsights = this.getErrorMessage(error);
          this.loadingInsights = false;
        }
      });
  }



  /**
   * Get color for entity
   */
  getEntityColor(index: number): string {
    const colors = this.chartService.getDefaultColors();
    return colors[index % colors.length];
  }

  /**
   * Get granularity label
   */
  getGranularityLabel(): string {
    return this.chartService.getGranularityLabel(this.granularity);
  }

  /**
   * Filter entities that have time series data (exclude Customer, etc.)
   */
  getTimeSeriesEntities(): EntityChartDataDto[] {
    if (this.cachedTimeSeriesEntities) {
      return this.cachedTimeSeriesEntities;
    }

    if (!this.cumulativeData?.entityData) return [];

    this.cachedTimeSeriesEntities = this.cumulativeData.entityData.filter(entity => {
      const entityName = entity.entityName?.toLowerCase() || '';
      // Exclude entities with no temporal data
      if (this.excludedEntities.includes(entityName)) return false;
      // Exclude entities that should only appear in cumulative view
      if (this.individualViewExcludedEntities.some(excluded => entityName.includes(excluded))) return false;
      return true;
    });

    return this.cachedTimeSeriesEntities;
  }

  /**
   * Get entities suitable for cumulative running total charts
   * (Appointment and ScrapCollection)
   */
  getCumulativeEntities(): string[] {
    return ['appointment', 'scrapcollection', 'subscription'];
  }

  /**
   * Check if an entity should show cumulative chart
   */
  isCumulativeEntity(entityName: string): boolean {
    const name = entityName?.toLowerCase() || '';
    return this.getCumulativeEntities().some(e => name.includes(e));
  }

  /**
   * Get filtered entities for cumulative running total view
   */
  getRunningTotalEntities(): EntityChartDataDto[] {
    if (this.cachedCumulativeEntities) {
      return this.cachedCumulativeEntities;
    }

    if (!this.cumulativeData?.entityData) return [];

    this.cachedCumulativeEntities = this.cumulativeData.entityData.filter(entity =>
      this.isCumulativeEntity(entity.entityName)
    );

    return this.cachedCumulativeEntities;
  }

  /**
   * Check if entity is subscription (for special handling)
   */
  isSubscriptionEntity(entityName: string): boolean {
    const name = entityName?.toLowerCase() || '';
    return name.includes('subscription');
  }

  /**
   * Get chart type for entity in individual view
   */
  getChartTypeForEntity(entityName: string): 'line' | 'bar' {
    return this.isSubscriptionEntity(entityName) ? 'line' : 'bar';
  }

  /**
   * Transform entity data to cumulative running total
   * For subscription, use cumulativeAmount from API (already calculated server-side)
   * For other entities, calculate cumulative count client-side
   */
  transformToCumulativeData(entity: EntityChartDataDto): EntityChartDataDto {
    if (!entity?.dataPoints?.length) return entity;

    const isSubscription = this.isSubscriptionEntity(entity.entityName);

    // For subscription, API already provides cumulativeAmount, so just use it
    if (isSubscription) {
      const cumulativeDataPoints = entity.dataPoints.map(dp => ({
        ...dp,
        value: (dp as any).cumulativeAmount ?? (dp as any).amount ?? dp.value ?? 0,
        count: (dp as any).cumulativeAmount ?? (dp as any).amount ?? dp.value ?? 0,
        total: (dp as any).cumulativeAmount ?? (dp as any).amount ?? dp.value ?? 0
      }));

      return {
        ...entity,
        entityName: `${entity.entityName} (Cumulative)`,
        dataPoints: cumulativeDataPoints
      };
    }

    // For other entities, calculate cumulative count client-side
    let runningTotal = 0;
    const cumulativeDataPoints = entity.dataPoints.map(dp => {
      const value = this.getDataPointValue(dp);
      runningTotal += typeof value === 'number' ? value : parseFloat(value) || 0;

      return {
        ...dp,
        value: runningTotal,
        count: runningTotal,
        total: runningTotal
      };
    });

    return {
      ...entity,
      entityName: `${entity.entityName} (Cumulative)`,
      dataPoints: cumulativeDataPoints
    };
  }

  /**
   * Get cumulative running total data for charts
   */
  getCumulativeRunningTotalData(): EntityChartDataDto[] {
    if (this.cachedCumulativeRunningTotal) {
      return this.cachedCumulativeRunningTotal;
    }

    this.cachedCumulativeRunningTotal = this.getRunningTotalEntities().map(entity =>
      this.transformToCumulativeData(entity)
    );

    return this.cachedCumulativeRunningTotal;
  }

  /**
   * Get insights daily chart data (multi-series)
   */
  getInsightsDailyChartData(): EntityChartDataDto[] {
    if (this.cachedInsightsDailyData) {
      return this.cachedInsightsDailyData;
    }

    if (!this.insightsData?.dataPoints?.length) return [];

    const dataPoints = this.insightsData.dataPoints;

    this.cachedInsightsDailyData = [
      {
        entityName: 'Collection Count',
        totalCount: this.insightsData.summary?.totalCollections || 0,
        dataPoints: dataPoints.map(dp => ({
          label: this.formatDateLabel(dp.date),
          value: dp.collectionCount,
          date: dp.date
        }))
      },
      {
        entityName: 'Grand Total (₹)',
        totalCount: this.insightsData.summary?.totalGrandTotal || 0,
        dataPoints: dataPoints.map(dp => ({
          label: this.formatDateLabel(dp.date),
          value: dp.grandTotal,
          date: dp.date
        }))
      },
      {
        entityName: 'Scrap Value (₹)',
        totalCount: this.insightsData.summary?.totalScrapValue || 0,
        dataPoints: dataPoints.map(dp => ({
          label: this.formatDateLabel(dp.date),
          value: dp.scrapValue,
          date: dp.date
        }))
      }
    ];

    return this.cachedInsightsDailyData;
  }

  /**
   * Get insights cumulative chart data (multi-series)
   */
  getInsightsCumulativeChartData(): EntityChartDataDto[] {
    if (this.cachedInsightsCumulativeData) {
      return this.cachedInsightsCumulativeData;
    }

    if (!this.insightsData?.dataPoints?.length) return [];

    const dataPoints = this.insightsData.dataPoints;

    this.cachedInsightsCumulativeData = [
      {
        entityName: 'Cumulative Collections',
        totalCount: this.insightsData.summary?.totalCollections || 0,
        dataPoints: dataPoints.map(dp => ({
          label: this.formatDateLabel(dp.date),
          value: dp.cumulativeCollectionCount,
          date: dp.date
        }))
      },
      {
        entityName: 'Cumulative Grand Total (₹)',
        totalCount: this.insightsData.summary?.totalGrandTotal || 0,
        dataPoints: dataPoints.map(dp => ({
          label: this.formatDateLabel(dp.date),
          value: dp.cumulativeGrandTotal,
          date: dp.date
        }))
      },
      {
        entityName: 'Cumulative Scrap Value (₹)',
        totalCount: this.insightsData.summary?.totalScrapValue || 0,
        dataPoints: dataPoints.map(dp => ({
          label: this.formatDateLabel(dp.date),
          value: dp.cumulativeScrapValue,
          date: dp.date
        }))
      }
    ];

    return this.cachedInsightsCumulativeData;
  }

  /**
   * Get insights revenue breakdown chart data
   */
  getInsightsRevenueBreakdownData(): EntityChartDataDto[] {
    if (this.cachedInsightsRevenueData) {
      return this.cachedInsightsRevenueData;
    }

    if (!this.insightsData?.dataPoints?.length) return [];

    const dataPoints = this.insightsData.dataPoints;

    this.cachedInsightsRevenueData = [
      {
        entityName: 'Scrap Value',
        totalCount: this.insightsData.summary?.totalScrapValue || 0,
        dataPoints: dataPoints.map(dp => ({
          label: this.formatDateLabel(dp.date),
          value: dp.scrapValue,
          date: dp.date
        }))
      },
      {
        entityName: 'Platform Charges',
        totalCount: this.insightsData.summary?.totalPlatformCharges || 0,
        dataPoints: dataPoints.map(dp => ({
          label: this.formatDateLabel(dp.date),
          value: dp.platformCharges,
          date: dp.date
        }))
      },
      {
        entityName: 'Collection Charges',
        totalCount: this.insightsData.summary?.totalCollectionCharges || 0,
        dataPoints: dataPoints.map(dp => ({
          label: this.formatDateLabel(dp.date),
          value: dp.collectionCharges,
          date: dp.date
        }))
      }
    ];

    return this.cachedInsightsRevenueData;
  }

  /**
   * Format date for chart labels
   */
  private formatDateLabel(dateStr: string): string {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-IN', {
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  }

  /**
   * Retry loading insights data
   */
  retryInsightsLoad = (): void => {
    this.loadInsightsData();
  }

  /**
   * Get error message from error object
   */
  private getErrorMessage(error: any): string {
    if (error.status === 401) {
      return 'Authentication required. Please log in again.';
    } else if (error.status === 403) {
      return 'Access denied. You do not have permission to view this data.';
    } else if (error.status === 404) {
      return 'Data not found.';
    } else if (error.status >= 500) {
      return 'Server error. Please try again later.';
    } else if (error.status === 0) {
      return 'Network error. Please check your connection.';
    }
    return 'Failed to load data. Please try again.';
  }

  /**
   * Retry loading cumulative data
   */
  retryCumulativeLoad = (): void => {
    this.loadCumulativeData();
  }



  /**
   * Get label from data point (handles different API formats)
   */
  private getDataPointLabel(dp: any): string {
    return dp.label || dp.date || dp.period || dp.timestamp || dp.name || '';
  }

  /**
   * Get value from data point (handles different API formats)
   */
  private getDataPointValue(dp: any): number {
    const value = dp.value ?? dp.count ?? dp.total ?? dp.amount ?? 0;
    return typeof value === 'number' ? value : parseFloat(value) || 0;
  }

  /**
   * Export chart data to CSV
   */
  exportToCSV(): void {
    if (!this.cumulativeData?.entityData?.length) return;

    // Get all unique labels
    const allLabels = new Set<string>();
    this.cumulativeData.entityData.forEach(entity => {
      entity.dataPoints?.forEach(dp => {
        const label = this.getDataPointLabel(dp);
        if (label) allLabels.add(label);
      });
    });
    const labels = Array.from(allLabels).sort();

    // Build CSV content
    const headers = ['Date', ...this.cumulativeData.entityData.map(e => e.entityName)];
    const rows = labels.map(label => {
      const row = [label];
      this.cumulativeData!.entityData.forEach(entity => {
        const point = entity.dataPoints?.find(dp => this.getDataPointLabel(dp) === label);
        row.push(point ? this.getDataPointValue(point).toString() : '0');
      });
      return row;
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `cumulative-charts-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * TrackBy function for entity lists to improve ngFor performance
   */
  trackByEntityName(index: number, entity: EntityChartDataDto): string {
    return entity.entityName || index.toString();
  }

  toggleAgent(agentId: string): void {
    const index = this.selectedAgents.indexOf(agentId);
    if (index > -1) {
      this.selectedAgents.splice(index, 1);
    } else {
      this.selectedAgents.push(agentId);
    }
  }

  isAgentSelected(agentId: string): boolean {
    return this.selectedAgents.includes(agentId);
  }

  getAgentDisplayText(): string {
    if (this.selectedAgents.length === 0) {
      return 'All Agents';
    }
    if (this.selectedAgents.length === 1) {
      return (
        this.agents.find((a) => a.value === this.selectedAgents[0])
          ?.label || 'Selected'
      );
    }
    return `${this.selectedAgents.length} selected`;
  }

  toggleAgentDropdown(): void {
    this.agentDropdownOpen = !this.agentDropdownOpen;
  }

  closeAgentDropdown(): void {
    this.agentDropdownOpen = false;
  }

}
